import React, { useState } from 'react';
import { Quote, Star, ChevronLeft, ChevronRight } from 'lucide-react';
interface TestimonialsProps {
  language: 'en' | 'es';
}
export function Testimonials({
  language
}: TestimonialsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const content = {
    en: {
      title: 'What Our Clients Say',
      subtitle: 'Real Stories from Business Owners We Have Helped',
      trustText: 'Trusted by 1,000+ U.S. Business Owners — A+ Support (Se habla español)',
      testimonials: [{
        text: 'I applied at 9 a.m. and had funds by 3 p.m. The process was incredibly smooth and professional. Sparrow Advance saved my business during a critical cash flow crunch.',
        author: 'Luis M.',
        location: 'Miami, FL',
        business: 'Restaurant Owner'
      }, {
        text: 'They explained everything in Spanish — total lifesaver. No hidden fees, no surprises. Just honest, transparent funding when I needed it most.',
        author: 'Carla D.',
        location: 'Chicago, IL',
        business: 'Retail Store Owner'
      }, {
        text: 'Banks said no. Sparrow said yes in under an hour. The team was supportive, professional, and made me feel like a priority, not just a number.',
        author: 'Jake T.',
        location: 'Phoenix, AZ',
        business: 'Construction Company'
      }, {
        text: 'Banks said no, Sparrow said yes in 2 hours. Total game-changer. I was able to make payroll and keep my team together during a tough month.',
        author: 'Rosa G.',
        location: 'Dallas, TX',
        business: 'Cleaning Services'
      }, {
        text: 'My advisor explained everything in Spanish. That made all the difference. I felt understood and respected throughout the entire process.',
        author: 'Hector L.',
        location: 'Orlando, FL',
        business: 'Landscaping Company'
      }, {
        text: 'Fast, professional, and transparent. Sparrow Advance delivered exactly what they promised. No surprises, no hidden fees, just honest funding.',
        author: 'Maria S.',
        location: 'Los Angeles, CA',
        business: 'Catering Business'
      }]
    },
    es: {
      title: 'Lo Que Dicen Nuestros Clientes',
      subtitle: 'Historias Reales de Dueños de Negocios que Hemos Ayudado',
      trustText: 'Confiado por 1,000+ Dueños de Negocios en EE.UU. — Soporte A+ (We speak English)',
      testimonials: [{
        text: 'Apliqué a las 9 a.m. y tenía fondos a las 3 p.m. El proceso fue increíblemente fluido y profesional. Sparrow Advance salvó mi negocio durante una crisis de flujo de efectivo crítica.',
        author: 'Luis M.',
        location: 'Miami, FL',
        business: 'Dueño de Restaurante'
      }, {
        text: 'Explicaron todo en español — un salvavidas total. Sin tarifas ocultas, sin sorpresas. Solo financiamiento honesto y transparente cuando más lo necesitaba.',
        author: 'Carla D.',
        location: 'Chicago, IL',
        business: 'Dueña de Tienda Minorista'
      }, {
        text: 'Los bancos dijeron que no. Sparrow dijo que sí en menos de una hora. El equipo fue solidario, profesional y me hizo sentir como una prioridad, no solo un número.',
        author: 'Jake T.',
        location: 'Phoenix, AZ',
        business: 'Empresa de Construcción'
      }, {
        text: 'Los bancos dijeron que no, Sparrow dijo que sí en 2 horas. Cambió todo. Pude hacer la nómina y mantener a mi equipo unido durante un mes difícil.',
        author: 'Rosa G.',
        location: 'Dallas, TX',
        business: 'Servicios de Limpieza'
      }, {
        text: 'Mi asesor explicó todo en español. Eso marcó toda la diferencia. Me sentí comprendido y respetado durante todo el proceso.',
        author: 'Hector L.',
        location: 'Orlando, FL',
        business: 'Empresa de Jardinería'
      }, {
        text: 'Rápido, profesional y transparente. Sparrow Advance cumplió exactamente lo que prometió. Sin sorpresas, sin tarifas ocultas, solo financiamiento honesto.',
        author: 'Maria S.',
        location: 'Los Angeles, CA',
        business: 'Negocio de Catering'
      }]
    }
  };
  const t = content[language];
  const nextTestimonial = () => {
    setCurrentIndex(prev => (prev + 1) % t.testimonials.length);
  };
  const prevTestimonial = () => {
    setCurrentIndex(prev => (prev - 1 + t.testimonials.length) % t.testimonials.length);
  };
  return <section className="w-full py-20 bg-gradient-to-b from-gray-50 to-white" data-id="element-259">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-260">
        <div className="text-center mb-16" data-id="element-261">
          <h2 className="text-4xl sm:text-5xl font-bold text-[#142B44] mb-4" data-id="element-262">
            {t.title}
          </h2>
          <p className="text-xl text-gray-600" data-id="element-263">{t.subtitle}</p>
        </div>
        <div className="relative max-w-4xl mx-auto" data-id="element-264">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-12" data-id="element-265">
            <div className="flex items-center justify-between mb-8" data-id="element-266">
              <Quote className="text-[#2D5D9F]" size={48} strokeWidth={1.5} data-id="element-267" />
              <div className="flex space-x-1" data-id="element-268">
                {[...Array(5)].map((_, i) => <Star key={i} size={24} className="fill-[#FFD700] text-[#FFD700]" data-id="element-269" />)}
              </div>
            </div>
            <p className="text-gray-700 mb-8 leading-relaxed text-xl font-medium italic" data-id="element-270">
              "{t.testimonials[currentIndex].text}"
            </p>
            <div className="border-t border-gray-200 pt-6" data-id="element-271">
              <p className="font-bold text-[#142B44] text-2xl" data-id="element-272">
                {t.testimonials[currentIndex].author}
              </p>
              <p className="text-base text-gray-600 mt-2" data-id="element-273">
                {t.testimonials[currentIndex].business}
              </p>
              <p className="text-base text-gray-500 mt-1" data-id="element-274">
                {t.testimonials[currentIndex].location}
              </p>
            </div>
          </div>
          <button onClick={prevTestimonial} className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-[#142B44] hover:bg-[#142B44] hover:text-white transition-all hover:scale-110" data-id="element-275">
            <ChevronLeft size={24} data-id="element-276" />
          </button>
          <button onClick={nextTestimonial} className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-xl flex items-center justify-center text-[#142B44] hover:bg-[#142B44] hover:text-white transition-all hover:scale-110" data-id="element-277">
            <ChevronRight size={24} data-id="element-278" />
          </button>
          <div className="flex justify-center mt-8 space-x-2" data-id="element-279">
            {t.testimonials.map((_, index) => <button key={index} onClick={() => setCurrentIndex(index)} className={`w-3 h-3 rounded-full transition-all ${index === currentIndex ? 'bg-[#142B44] w-8' : 'bg-gray-300 hover:bg-gray-400'}`} data-id="element-280" />)}
          </div>
        </div>
        <p className="text-center text-lg text-gray-700 font-semibold mt-12" data-id="element-281">
          {t.trustText}
        </p>
      </div>
    </section>;
}